import { useState, useEffect } from 'react';

import { useRouter, usePathname, useSearchParams } from 'src/routes/hooks';

import { SplashScreen } from 'src/components/loading-screen';

import { useAuthContext } from '../hooks';

// ----------------------------------------------------------------------

type AuthGuardProps = {
  children: React.ReactNode;
};

const LOGIN_PATH = '/';
const INVITE_TOKEN_STORAGE_KEY = 'pending-invite-token';
const INVITE_GROUP_ID_STORAGE_KEY = 'pending-invite-group-id';

export function AuthGuard({ children }: AuthGuardProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const { authenticated, loading } = useAuthContext();

  const [isChecking, setIsChecking] = useState(true);

  const createRedirectPath = (currentPath: string) => {
    const queryString = new URLSearchParams({ returnTo: pathname }).toString();
    return `${currentPath}?${queryString}`;
  };

  const checkPermissions = async (): Promise<void> => {
    if (loading) {
      return;
    }

    if (!authenticated) {
      // Store invitation token before redirecting to login
      // Check if this is an invitation link
      const inviteToken = searchParams.get('t');
      const inviteGroupMatch = pathname.match(/^\/invite\/group\/([^/]+)$/);

      if (inviteToken && inviteGroupMatch) {
        const groupId = inviteGroupMatch[1];
        sessionStorage.setItem(INVITE_TOKEN_STORAGE_KEY, inviteToken);
        sessionStorage.setItem(INVITE_GROUP_ID_STORAGE_KEY, groupId);
      }

      const redirectPath = createRedirectPath(LOGIN_PATH);

      router.replace(redirectPath);

      return;
    }

    setIsChecking(false);
  };

  useEffect(() => {
    checkPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, loading]);

  if (isChecking) {
    return <SplashScreen />;
  }

  return <>{children}</>;
}
